/* eslint-disable standard/computed-property-even-spacing */
import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import JwtService from '@/common/jwt.service'
import RefreshTokenService from '@/common/refresh.service.js'
import { API_URL } from '@/common/config'
import { MessageBox, Message } from 'element-ui'
import { HEADER_X_IS_MAINTENANCE, HTTP_STATUS_MAINTENANCE } from '@/common/constance'
import { SET_MAINTENANCE } from '@/store/mutations.type'

// Matches the backend's withdrawal-rejection message (AuthorizationFilter) by string, rather than
// a dedicated response field/code.
const MEMBER_WITHDRAWN_RESPONSE_MESSAGE = '退会しました。'
const MEMBER_WITHDRAWN_FLASH_MESSAGE = '退会済みのため、ログアウトしました。'

// The wire status is 400, not 403: CloudFront (staging/production) rewrites 403/404 origin
// responses into a 200 SPA error page, so the backend deliberately no longer returns 403 here.
// The semantic 403 is still carried in the response body's own `code` field; the message match
// below is what actually discriminates a withdrawal from any other 400.
export const isWithdrawalResponse = (error) =>
  !!(error.response && error.response.status === 400 && error.response.data && error.response.data.message === MEMBER_WITHDRAWN_RESPONSE_MESSAGE)

// One-time latch, never auto-reset during a page's lifetime - withdrawal is terminal, so several
// in-flight requests failing for the same reason around the same instant should only trigger the
// flash/redirect once.
let withdrawalHandled = false

export const handleWithdrawalViolation = () => {
  if (withdrawalHandled) {
    return
  }
  withdrawalHandled = true
  JwtService.destroyAll()
  ApiService.setHeader()
  Message({ message: MEMBER_WITHDRAWN_FLASH_MESSAGE, type: 'warning', duration: 5000 })
  // Reuses routeMember.js's own sessionViolation query param - its beforeEach guard opens the
  // login dialog only after CHECK_AUTH_CLIENT has settled for this navigation.
  window.app.$router.push({ path: '/portal', query: { sessionViolation: '1' } })
}

// Test-only: module-level state persists across test cases in the same Jest worker otherwise.
export const __resetWithdrawalHandledForTests = () => {
  withdrawalHandled = false
}

const ApiService = {
  init () {
    Vue.use(VueAxios, axios)
    Vue.axios.defaults.baseURL = API_URL

    Vue.axios.interceptors.response.use((response) => {
      const isMaintenance = (response.status === HTTP_STATUS_MAINTENANCE &&
                             response.headers[HEADER_X_IS_MAINTENANCE] === '1')
      window.app.$store.commit(SET_MAINTENANCE, isMaintenance)
      return response
    }, async (error) => {
      const isMaintenance = (error.response.status === HTTP_STATUS_MAINTENANCE &&
                             error.response.headers[HEADER_X_IS_MAINTENANCE] === '1')
      window.app.$store.commit(SET_MAINTENANCE, isMaintenance)
      if (isMaintenance) {
          return false
      }

      if (error.response.status === 401) {
        // Access token expired - refresh it, then retry the original request.
        return RefreshTokenService.getNewAccessToken(error.config)
      } else if (isWithdrawalResponse(error)) {
        handleWithdrawalViolation()
        return Promise.reject(error)
      } else {
        const host = window.location.hostname
        const DOMAIN_ADMIN = process.env.VUE_APP_DOMAIN_ADMIN
        if (host === DOMAIN_ADMIN && error.response.status >= 500) {
          MessageBox({
            title: 'エラーが発生しました。(エラーコード: ' + error.response.status + ')',
            type: 'エラー',
            showClose: false,
            center: true
          })
        }
        return Promise.reject(error)
      }
    })
  },

  setHeader () {
    // Vue.axios.defaults.headers.common is a global, in-memory axios default - it isn't tied to
    // the cookie/localStorage JwtService.destroyAll() clears, and nothing else resets it. Delete
    // the header entirely when there's no real token, rather than writing a stringified `null`/
    // `undefined` or leaving a stale token attached to later, unrelated requests (including
    // unauthenticated ones, which the backend would then reject outright).
    const token = JwtService.getToken()
    if (token) {
      Vue.axios.defaults.headers.common['Authorization'] = `${token}`
    } else {
      delete Vue.axios.defaults.headers.common['Authorization']
    }
  },

  query (resource, params) {
    return Vue.axios.get(resource, params).catch(error => {
      throw error
    })
  },

  get (resource, slug = '') {
    return Vue.axios.get(`${resource}/${slug}`).catch(error => {
      throw error
    })
  },
  getList (resource) {
        return Vue.axios.get(`${resource}`).catch(error => {
            throw error
        })
  },

  post (resource, params) {
    return Vue.axios.post(`${resource}`, params)
  },

  update (resource, slug, params) {
    return Vue.axios.put(`${resource}/${slug}`, params)
  },

  put (resource, params) {
    return Vue.axios.put(`${resource}`, params)
  },

  delete (resource) {
    return Vue.axios.delete(resource).catch(error => {
      throw error
    })
  }
}

export default ApiService
